import {
  FETCH_BANK_USER_DETAILS,
  FETCH_BANK_USER_DETAILS_SUCCESS,
  FETCH_BANK_USER_DETAILS_FAILED,
  ADD_BANK_USER_DETAIL,
  ADD_BANK_USER_DETAIL_SUCCESS,
  ADD_BANK_USER_DETAIL_FAILED,
  EDIT_BANK_USER_DETAIL,
  EDIT_BANK_USER_DETAIL_SUCCESS,
  EDIT_BANK_USER_DETAIL_FAILED,
  DELETE_BANK_USER_DETAIL,
  DELETE_BANK_USER_DETAIL_SUCCESS,
  DELETE_BANK_USER_DETAIL_FAILED,
  SEARCH_BANK_USER_DETAILS,
  FILTER_BANK_USER_TYPE,
  FILTER_BANK_USER_STATUS,
  CLEAR_BANK_USER_FILTERS,
  CLEAR_BANK_USER_ERROR
} from "../store/types";

import { firebase } from '../config/configureFirebase';
import { onValue, set, push, update, remove, get, query, orderByChild, equalTo } from "firebase/database";

export const fetchBankUserDetails = () => (dispatch) => {
  const {
    bankUserDetailsRef
  } = firebase;

  // Add validation to prevent undefined reference errors
  if (!bankUserDetailsRef) {
    console.error('bankUserDetailsRef is not defined in Firebase config');
    dispatch({
      type: FETCH_BANK_USER_DETAILS_FAILED,
      payload: "Firebase configuration error: bankUserDetailsRef not found",
    });
    return;
  }

  dispatch({
    type: FETCH_BANK_USER_DETAILS,
    payload: null
  });

  onValue(bankUserDetailsRef, (snapshot) => {
    if (snapshot.val()) {
      const data = snapshot.val();
      const arr = Object.keys(data).map(i => {
        data[i].id = i;
        return data[i];
      });
      
      // Sort by creation date (newest first)
      const sortedArr = arr.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      
      dispatch({
        type: FETCH_BANK_USER_DETAILS_SUCCESS,
        payload: sortedArr
      });
    } else {
      dispatch({
        type: FETCH_BANK_USER_DETAILS_FAILED,
        payload: "No bank user details available."
      });
    }
  }, (error) => {
    dispatch({
      type: FETCH_BANK_USER_DETAILS_FAILED,
      payload: "Error fetching bank user details: " + error.message
    });
  });
};

export const fetchBankUserDetailsByUser = (userId) => (dispatch) => {
  const {
    bankUserDetailsRef
  } = firebase;

  // Add validation to prevent undefined reference errors
  if (!bankUserDetailsRef) {
    console.error('bankUserDetailsRef is not defined in Firebase config');
    dispatch({
      type: FETCH_BANK_USER_DETAILS_FAILED,
      payload: "Firebase configuration error: bankUserDetailsRef not found",
    });
    return;
  }

  dispatch({
    type: FETCH_BANK_USER_DETAILS,
    payload: null
  });

  // Use query to filter by userId instead of separate path
  const userBankDetailsQuery = query(
    bankUserDetailsRef,
    orderByChild('userId'),
    equalTo(userId)
  );

  onValue(userBankDetailsQuery, (snapshot) => {
    if (snapshot.val()) {
      const data = snapshot.val();
      const arr = Object.keys(data).map(i => {
        data[i].id = i;
        return data[i];
      });
      
      // Sort by creation date (newest first)
      const sortedArr = arr.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      
      dispatch({
        type: FETCH_BANK_USER_DETAILS_SUCCESS,
        payload: sortedArr
      });
    } else {
      dispatch({
        type: FETCH_BANK_USER_DETAILS_SUCCESS,
        payload: []
      });
    }
  }, (error) => {
    dispatch({
      type: FETCH_BANK_USER_DETAILS_FAILED,
      payload: "Error fetching user bank details: " + error.message
    });
  });
};

export const fetchBankUserDetailsByType = (userType) => (dispatch) => {
  const {
    bankUserDetailsByTypeRef
  } = firebase;

  // Add validation to prevent undefined reference errors
  if (!bankUserDetailsByTypeRef) {
    console.error('bankUserDetailsByTypeRef is not defined in Firebase config');
    dispatch({
      type: FETCH_BANK_USER_DETAILS_FAILED,
      payload: "Firebase configuration error: bankUserDetailsByTypeRef not found",
    });
    return;
  }

  dispatch({
    type: FETCH_BANK_USER_DETAILS,
    payload: null
  });

  onValue(bankUserDetailsByTypeRef(userType), (snapshot) => {
    if (snapshot.val()) {
      const data = snapshot.val();
      const arr = Object.keys(data).map(i => {
        data[i].id = i;
        return data[i];
      });
      
      dispatch({
        type: FETCH_BANK_USER_DETAILS_SUCCESS,
        payload: arr
      });
    } else {
      dispatch({
        type: FETCH_BANK_USER_DETAILS_SUCCESS,
        payload: []
      });
    }
  }, (error) => {
    dispatch({
      type: FETCH_BANK_USER_DETAILS_FAILED,
      payload: "Error fetching bank details by type: " + error.message
    });
  });
};

export const addBankUserDetail = (bankDetail) => (dispatch) => {
  const {
    bankUserDetailsRef
  } = firebase;

  // Add validation to prevent undefined reference errors
  if (!bankUserDetailsRef) {
    console.error('bankUserDetailsRef is not defined in Firebase config');
    dispatch({
      type: ADD_BANK_USER_DETAIL_FAILED,
      payload: "Firebase configuration error: bankUserDetailsRef not found",
    });
    return;
  }

  dispatch({
    type: ADD_BANK_USER_DETAIL,
    payload: bankDetail
  });

  // Clean bankDetail data - remove undefined values
  const cleanBankDetail = {};
  Object.keys(bankDetail).forEach(key => {
    if (bankDetail[key] !== undefined && bankDetail[key] !== null) {
      cleanBankDetail[key] = bankDetail[key];
    }
  });

  // Add timestamp and metadata
  const bankDetailWithMetadata = {
    ...cleanBankDetail,
    createdAt: cleanBankDetail.createdAt || new Date().getTime(),
    updatedAt: new Date().getTime(),
    createdBy: firebase.auth?.currentUser?.uid || 'system',
    status: cleanBankDetail.status || 'active'  // Changed from 'pending' to 'active'
  };

  const newBankDetailRef = push(bankUserDetailsRef);
  set(newBankDetailRef, bankDetailWithMetadata)
    .then(() => {
      dispatch({
        type: ADD_BANK_USER_DETAIL_SUCCESS,
        payload: { ...bankDetailWithMetadata, id: newBankDetailRef.key }
      });
    })
    .catch((error) => {
      console.error('Error adding bank user detail:', error);
      dispatch({
        type: ADD_BANK_USER_DETAIL_FAILED,
        payload: `Error adding bank user detail: ${error.message}`
      });
    });
};

export const editBankUserDetail = (id, bankDetail) => (dispatch) => {
  const {
    bankUserDetailsEditRef
  } = firebase;

  // Add validation to prevent undefined reference errors
  if (!bankUserDetailsEditRef) {
    console.error('bankUserDetailsEditRef is not defined in Firebase config');
    dispatch({
      type: EDIT_BANK_USER_DETAIL_FAILED,
      payload: "Firebase configuration error: bankUserDetailsEditRef not found",
    });
    return;
  }

  dispatch({
    type: EDIT_BANK_USER_DETAIL,
    payload: { id, ...bankDetail }
  });

  // Clean bankDetail data - remove undefined values
  const cleanBankDetail = {};
  Object.keys(bankDetail).forEach(key => {
    if (bankDetail[key] !== undefined && bankDetail[key] !== null) {
      cleanBankDetail[key] = bankDetail[key];
    }
  });

  // Add update timestamp and ensure status is active for user updates
  const updatedBankDetail = {
    ...cleanBankDetail,
    updatedAt: new Date().getTime(),
    updatedBy: firebase.auth?.currentUser?.uid || 'system',
    status: cleanBankDetail.status || 'active'  // Ensure status is active when user updates
  };

  update(bankUserDetailsEditRef(id), updatedBankDetail)
    .then(() => {
      dispatch({
        type: EDIT_BANK_USER_DETAIL_SUCCESS,
        payload: { id, ...updatedBankDetail }
      });
    })
    .catch((error) => {
      console.error('Error updating bank user detail:', error);
      dispatch({
        type: EDIT_BANK_USER_DETAIL_FAILED,
        payload: `Error updating bank user detail: ${error.message}`
      });
    });
};

export const deleteBankUserDetail = (id) => (dispatch) => {
  const {
    bankUserDetailsEditRef
  } = firebase;

  // Add validation to prevent undefined reference errors
  if (!bankUserDetailsEditRef) {
    console.error('bankUserDetailsEditRef is not defined in Firebase config');
    dispatch({
      type: DELETE_BANK_USER_DETAIL_FAILED,
      payload: "Firebase configuration error: bankUserDetailsEditRef not found",
    });
    return;
  }

  dispatch({
    type: DELETE_BANK_USER_DETAIL,
    payload: id
  });

  remove(bankUserDetailsEditRef(id))
    .then(() => {
      dispatch({
        type: DELETE_BANK_USER_DETAIL_SUCCESS,
        payload: id
      });
    })
    .catch((error) => {
      console.error('Error deleting bank user detail:', error);
      dispatch({
        type: DELETE_BANK_USER_DETAIL_FAILED,
        payload: `Error deleting bank user detail: ${error.message}`
      });
    });
};

export const updateBankUserDetailStatus = (id, status) => (dispatch) => {
  const {
    bankUserDetailsEditRef
  } = firebase;

  // Add validation to prevent undefined reference errors
  if (!bankUserDetailsEditRef) {
    console.error('bankUserDetailsEditRef is not defined in Firebase config');
    dispatch({
      type: EDIT_BANK_USER_DETAIL_FAILED,
      payload: "Firebase configuration error: bankUserDetailsEditRef not found",
    });
    return;
  }

  // Validate status
  if (!status || status === undefined || status === null) {
    console.error('Status is required for update');
    dispatch({
      type: EDIT_BANK_USER_DETAIL_FAILED,
      payload: "Status is required for update",
    });
    return;
  }

  const statusUpdate = {
    status: status,
    statusUpdatedAt: new Date().getTime(),
    statusUpdatedBy: firebase.auth?.currentUser?.uid || 'system'
  };

  update(bankUserDetailsEditRef(id), statusUpdate)
    .then(() => {
      dispatch({
        type: EDIT_BANK_USER_DETAIL_SUCCESS,
        payload: { id, ...statusUpdate }
      });
    })
    .catch((error) => {
      console.error('Error updating bank user detail status:', error);
      dispatch({
        type: EDIT_BANK_USER_DETAIL_FAILED,
        payload: `Error updating status: ${error.message}`
      });
    });
};

export const searchBankUserDetails = (searchTerm) => (dispatch) => {
  dispatch({
    type: SEARCH_BANK_USER_DETAILS,
    payload: searchTerm
  });
};

export const filterBankUserByType = (userType) => (dispatch) => {
  dispatch({
    type: FILTER_BANK_USER_TYPE,
    payload: userType
  });
};

export const filterBankUserByStatus = (status) => (dispatch) => {
  dispatch({
    type: FILTER_BANK_USER_STATUS,
    payload: status
  });
};

export const clearBankUserFilters = () => (dispatch) => {
  dispatch({
    type: CLEAR_BANK_USER_FILTERS,
    payload: null
  });
};

export const getBankUserDetail = async (id) => {
  const {
    bankUserDetailsEditRef
  } = firebase;

  // Add validation to prevent undefined reference errors
  if (!bankUserDetailsEditRef) {
    console.error('bankUserDetailsEditRef is not defined in Firebase config');
    throw new Error('Firebase configuration error: bankUserDetailsEditRef not found');
  }

  // Validate id
  if (!id || id === undefined || id === null) {
    throw new Error('Bank user detail ID is required');
  }

  try {
    const snapshot = await get(bankUserDetailsEditRef(id));
    if (snapshot.val()) {
      return { id, ...snapshot.val() };
    } else {
      throw new Error('Bank user detail not found');
    }
  } catch (error) {
    console.error('Error fetching bank user detail:', error);
    throw error;
  }
};

export const clearBankUserError = () => (dispatch) => {
  dispatch({
    type: CLEAR_BANK_USER_ERROR,
    payload: null
  });
};